import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRewards, type RewardsData, type PointsHistoryItem } from "@/hooks/useRewards";
import RewardsHero from "@/pages/rewards/components/RewardsHero";
import TiersSection from "@/pages/rewards/components/TiersSection";
import HowToEarn from "@/pages/rewards/components/HowToEarn";
import PointsHistory from "@/pages/rewards/components/PointsHistory";
import RewardsGuest from "@/pages/rewards/components/RewardsGuest";

const REWARDS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "برنامج المكافآت — متجر الإبداع",
  "description": "اكسب نقاطاً مع كل عملية شراء وارتقِ بمستواك من برونزي إلى بلاتيني. استمتع بمزايا حصرية وخصومات في متجر الإبداع.",
  "url": `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/rewards`,
  "inLanguage": "ar-SA",
  "isPartOf": {
    "@type": "WebSite",
    "name": "متجر الإبداع AL Ebdaa",
    "url": import.meta.env.VITE_SITE_URL ?? "https://example.com"
  }
};

export default function RewardsPage() {
  const { user, isLoggedIn } = useAuth();
  const { getRewards, getPointsHistory } = useRewards();
  const navigate = useNavigate();

  const [rewards, setRewards] = useState<RewardsData | null>(null);
  const [history, setHistory] = useState<PointsHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-rewards";
    script.textContent = JSON.stringify(REWARDS_SCHEMA);
    document.head.appendChild(script);
    document.title = "برنامج المكافآت — متجر الإبداع AL Ebdaa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "اكسب نقاطاً مع كل عملية شراء وارتقِ بمستواك من برونزي إلى بلاتيني. استمتع بمزايا حصرية وخصومات في متجر الإبداع AL Ebdaa.");
    return () => {
      const el = document.getElementById("schema-rewards");
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getRewards(user.id),
      getPointsHistory(user.id),
    ]).then(([r, h]) => {
      setRewards(r);
      setHistory(h);
      setLoading(false);
    });
  }, [isLoggedIn, user, getRewards, getPointsHistory]);

  // Guest view
  if (!isLoggedIn) {
    return <RewardsGuest />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-amber-400 text-3xl animate-spin"></i>
          <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>جاري تحميل نقاطك...</p>
        </div>
      </div>
    );
  }

  // Fallback if no rewards data
  if (!rewards) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 rounded-2xl">
            <i className="ri-error-warning-line text-amber-400 text-2xl"></i>
          </div>
          <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>تعذّر تحميل البيانات</p>
          <button
            onClick={() => window.location.reload()}
            className="text-amber-400 text-sm hover:underline cursor-pointer"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] px-4 py-16" dir="rtl">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-amber-400/4 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-[300px] h-[300px] bg-amber-400/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-2xl mx-auto">
        {/* Back + nav */}
        <div className="flex items-center justify-between mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-arrow-right-line"></i>
            العودة للمتجر
          </a>
          <button
            onClick={() => navigate("/account")}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/8 text-gray-400 hover:text-white text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-user-3-line text-sm"></i>
            حسابي
          </button>
        </div>

        {/* Hero card */}
        <div className="mb-5">
          <RewardsHero rewards={rewards} userName={user!.name} />
        </div>

        {/* Redeem CTA */}
        <button
          onClick={() => navigate("/redeem")}
          className="w-full flex items-center justify-between bg-gradient-to-l from-amber-400/15 to-amber-400/5 border border-amber-400/25 rounded-2xl px-5 py-4 mb-5 hover:border-amber-400/50 transition-all cursor-pointer group"
          style={{ fontFamily: "'Cairo', sans-serif" }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-xl">
              <i className="ri-exchange-dollar-line text-amber-400 text-lg"></i>
            </div>
            <div className="text-right">
              <p className="text-white text-sm font-black">استبدل نقاطك بكوبون خصم</p>
              <p className="text-gray-500 text-xs mt-0.5">50 نقطة = خصم 5% · 300 نقطة = خصم 20%</p>
            </div>
          </div>
          <i className="ri-arrow-left-line text-amber-400 text-lg group-hover:-translate-x-1 transition-transform"></i>
        </button>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-[#111] border border-white/8 rounded-2xl p-4 text-center">
            <p className={`text-xl font-black ${rewards.tierColor}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.points}
            </p>
            <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة حالية</p>
          </div>
          <div className="bg-[#111] border border-white/8 rounded-2xl p-4 text-center">
            <p className="text-xl font-black text-white" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.totalPointsEarned}
            </p>
            <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>إجمالي مكتسب</p>
          </div>
          <div className="bg-[#111] border border-white/8 rounded-2xl p-4 text-center">
            <p className="text-xl font-black text-green-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {history.length}
            </p>
            <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>طلب مكافأ</p>
          </div>
        </div>

        {/* Points history */}
        <div className="mb-5">
          <PointsHistory history={history} totalPoints={rewards.points} />
        </div>

        {/* Tiers */}
        <div className="mb-5">
          <TiersSection currentTier={rewards.tier} currentPoints={rewards.points} />
        </div>

        {/* How to earn */}
        <div className="mb-5">
          <HowToEarn />
        </div>

        {/* CTA shop */}
        <div className="bg-gradient-to-br from-amber-400/15 via-amber-400/5 to-transparent border border-amber-400/25 rounded-3xl p-6 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400/60 to-amber-400/0"></div>
          <div className="w-14 h-14 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-2xl mx-auto mb-4">
            <i className="ri-store-2-line text-amber-400 text-2xl"></i>
          </div>
          <p className="text-white text-base font-black mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            اكسب المزيد من النقاط
          </p>
          <p className="text-gray-500 text-xs mb-5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {rewards.nextTierPoints
              ? `${rewards.nextTierPoints} نقطة فقط تفصلك عن مستوى ${rewards.nextTierLabel}`
              : "أنت في أعلى مستوى — استمر في التسوق!"}
          </p>
          <button
            onClick={() => navigate("/")}
            className="flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-6 py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap mx-auto"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-shopping-cart-2-line"></i>
            تسوق الآن
          </button>
        </div>
      </div>
    </div>
  );
}
